import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoader } from "../../../store/loaderSlice";

const FullBetListSettingModal = ({ show, onClose }) => {
  const { marketId } = useParams();

  const [form, setForm] = useState({
    from: "",
    to: "",
    betType: "",
    amountMin: "",
    amountMax: "",
    marketType: "",
  });
  const dispatch = useDispatch();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===============================
  // Load Data
  // ===============================
  useEffect(() => {
    if (!show) return;

    document.body.style.overflow = "hidden";
    fetchInitialData();

    return () => (document.body.style.overflow = "auto");
  }, [show]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await dispatch(setLoader(true));
      const usersRes = await axiosInstance.get(`/user/my-downline`);
      const downlineUsers = usersRes.data.downnLine || [];
      setAllUsers(downlineUsers);

      const settingsRes = await axiosInstance.get(
        `/markets/settings/${marketId}`,
      );

      if (settingsRes.data) {
        const data = settingsRes.data;

        setForm({
          from: data.from_date
            ? new Date(data.from_date).toISOString().slice(0, 16)
            : "",
          to: data.to_date
            ? new Date(data.to_date).toISOString().slice(0, 16)
            : "",
          betType: data.bet_type || "",
          amountMin: data.amount_min || "",
          amountMax: data.amount_max || "",
          marketType: data.market_type || "",
        });

        if (data.users_bets) {
          const ids = data.users_bets
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);

          const matchedUsers = downlineUsers.filter((u) =>
            ids.includes(String(u._id)),
          );

          setSelectedUsers(matchedUsers);
        }
      }
      await dispatch(setLoader(false));
    } catch (err) {
      console.error("Settings Load Error:", err);
    } finally {
      setLoading(false);
      await dispatch(setLoader(false));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = () => {
    setForm({
      from: "",
      to: "",
      betType: "",
      amountMin: "",
      amountMax: "",
      marketType: "",
    });

    setSelectedUsers([]);
    setSearch("");
    setFilteredUsers([]);
  };

  // ===============================
  // Search Logic
  // ===============================
  useEffect(() => {
    if (!search) {
      setFilteredUsers([]);
      return;
    }

    const filtered = allUsers.filter(
      (user) =>
        user.uname.toLowerCase().includes(search.toLowerCase()) &&
        !selectedUsers.some((u) => u._id === user._id),
    );

    setFilteredUsers(filtered);
  }, [search, allUsers, selectedUsers]);

  const addUser = (user) => {
    setSelectedUsers((prev) => [...prev, user]);
    setSearch("");
    setFilteredUsers([]);
  };

  const removeUser = (id) => {
    setSelectedUsers((prev) =>
      prev.filter((u) => String(u._id) !== String(id)),
    );
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        from_date: form.from || null,
        to_date: form.to || null,
        bet_type: form.betType || null,
        amount_min: form.amountMin || null,
        amount_max: form.amountMax || null,
        market_type: form.marketType || null,
        user_bets: selectedUsers.map((u) => u._id),
      };

      await axiosInstance.post(`/markets/settings/${marketId}`, payload);

      onClose();
    } catch (err) {
      console.error("Save Error:", err);
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        .settings-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1050;
        }

        .settings-modal-wrapper {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1051;
        }

        .settings-modal {
          background: #fff;
          width: 95%;
          max-width: 700px;
          border-radius: 6px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.25);
          max-height: 90vh;
          overflow-y: auto;
          padding: 20px;
        }

        .tag {
          display: inline-flex;
          align-items: center;
          background: #007bff;
          color: #fff;
          padding: 3px 8px;
          border-radius: 15px;
          margin: 2px;
          font-size: 13px;
        }

        .tag span {
          margin-left: 6px;
          cursor: pointer;
          font-weight: bold;
        }

        .dropdown-suggestions {
          border: 1px solid #ddd;
          max-height: 150px;
          overflow-y: auto;
          background: #fff;
          position: absolute;
          width: 100%;
          z-index: 2000;
        }

        .dropdown-suggestions div {
          padding: 6px 10px;
          cursor: pointer;
        }

        .dropdown-suggestions div:hover {
          background: #f1f1f1;
        }
      `}</style>

      <div className="settings-modal-overlay" onClick={onClose} />

      <div className="settings-modal-wrapper">
        <div className="settings-modal">
          <h5 className="mb-3">Bet Settings</h5>

          {loading && <div style={{width:"100%",textAlign:"center",display: "block"}}>
      <img src="../../assets/images/loadinggif.gif" alt="Loader" />
      </div>}

          {/* Date */}
          <div className="form-row">
            <div className="form-group col-md-6">
              <label>From</label>
              <input
                type="datetime-local"
                className="form-control"
                name="from"
                value={form.from}
                onChange={handleChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label>To</label>
              <input
                type="datetime-local"
                className="form-control"
                name="to"
                value={form.to}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Bet Type */}
          <div className="form-group">
            <label>Bet Type</label>
            <select
              className="form-control"
              name="betType"
              value={form.betType}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="B">Back</option>
              <option value="L">Lay</option>
            </select>
          </div>

          {/* Amount */}
          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Amount Greater Than</label>
              <input
                type="number"
                className="form-control"
                name="amountMin"
                value={form.amountMin}
                onChange={handleChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label>Amount Less Than</label>
              <input
                type="number"
                className="form-control"
                name="amountMax"
                value={form.amountMax}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Market Type */}
          <div className="form-group">
            <label>Market Type</label>
            <select
              className="form-control"
              name="marketType"
              value={form.marketType}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="match_odd">Match Odd</option>
              <option value="bookmaker">BookMaker</option>
              <option value="fancy">Fancy</option>
              <option value="betfair_fancy">Betfair Fancy</option>
            </select>
          </div>

          {/* User Bets */}
          <div className="form-group position-relative">
            <label>User Bets</label>

            <div
              className="form-control d-flex flex-wrap"
              style={{ minHeight: "45px" }}
            >
              {selectedUsers.map((user) => (
                <div key={user._id} className="tag">
                  {user.uname}
                  <span onClick={() => removeUser(user._id)}>×</span>
                </div>
              ))}

              <input
                type="text"
                style={{ border: "none", outline: "none", flex: 1 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search user..."
              />
            </div>

            {filteredUsers.length > 0 && (
              <div className="dropdown-suggestions">
                {filteredUsers.map((user) => (
                  <div key={user._id} onClick={() => addUser(user)}>
                    {user.uname}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-right mt-3">
            <button className="btn btn-warning mr-2" onClick={handleClear}>
              Clear
            </button>

            <button className="btn btn-secondary mr-2" onClick={onClose}>
              Cancel
            </button>

            <button className="btn btn-primary" onClick={handleSubmit}>
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullBetListSettingModal;
